import type { IAnvändare } from 'src/types/nya/common';
import type { NavGroupProps, NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import type { NavData } from './nav-permissions';
import { ICONS } from './nav-icons';
import {
  BEHÖRIGHET,
  byggMenyKatalog,
  byggMenyTräd,
  filtreraNavigering,
  härledBehörigheter,
} from './nav-permissions';

export type { Behörighet, MenyKatalogPost, MenyKatalogNod } from './nav-permissions';
export { ärMenySynlig, ROLLER_FÖR_MENYINSTÄLLNINGAR } from './nav-permissions';

// ----------------------------------------------------------------------

/**
 * Sidebarens innehåll.
 *
 * Strukturen är lagrad efter vem posten är till för, inte efter teknisk domän:
 * en boende möter start + "Mitt boende" + "Föreningen" och inget mer, medan
 * styrelse, ekonomiansvariga och administratörer får sina sektioner ovanpå.
 * Håll toppnivån kort — en roll bör inte se fler än sju poster per sektion.
 *
 * Behörigheter:
 * - Löv ska alltid ange `allowedRoles` (annars är de osynliga för alla).
 * - Föräldrar utan `allowedRoles` ärver unionen av barnens, se
 *   {@link härledBehörigheter}. Sätt `allowedRoles` på en förälder bara när den
 *   ska vara en hård grind som barnen inte kan öppna.
 * - `menyNyckel` på en toppnivåpost gör den av/på-bar per organisation och
 *   plockas automatiskt upp av {@link MENY_KATALOG}.
 */
const NAVIGERING: NavData = [
  /**
   * Start — det alla ser först, oavsett roll.
   */
  {
    items: [
      {
        title: 'Hem',
        path: paths.dashboard.root,
        icon: ICONS.hem,
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        // Säljytan. Märkt `alla` med flit — vem som helst ska kunna se appen med
        // en påhittad förening — men med egen nyckel så en förening som redan är
        // igång kan stänga av den under Organisation → Inställningar.
        title: 'Demo',
        path: paths.dashboard.demo,
        icon: ICONS.demo,
        menyNyckel: 'demo',
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        title: 'Meddelanden',
        path: paths.dashboard.meddelanden.root,
        icon: ICONS.meddelanden,
        menyNyckel: 'meddelanden',
        children: [
          {
            title: 'Chattar',
            path: paths.dashboard.meddelanden.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Föreningens ämneskanaler. Kräver en förening — som privatperson
            // finns det inget att hämta.
            title: 'Kanaler',
            path: paths.dashboard.boende.kanaler,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Egen allowedRoles bredare än barnens med flit: filtreras alla barn bort blir
            // posten en ren länk till listan (se filtreraPost i nav-permissions.ts) — den
            // boende ser "Utskick" som en direkt länk, styrelsen ser hela undermenyn.
            title: 'Utskick',
            path: paths.dashboard.utskick.root,
            menyNyckel: 'utskick',
            allowedRoles: BEHÖRIGHET.BOENDE,
            children: [
              {
                title: 'Skapa',
                path: paths.dashboard.utskick.skapa,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
              {
                title: 'Skapa (ny)',
                path: paths.dashboard.utskick.skapaV2,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
              {
                title: 'Arkiv',
                path: paths.dashboard.utskick.arkiv,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
              {
                title: 'Inkorg',
                path: paths.dashboard.inkorg,
                allowedRoles: BEHÖRIGHET.STYRELSE,
              },
            ],
          },
        ],
      },
      {
        title: 'Ärenden',
        path: paths.dashboard.arende.root,
        icon: ICONS.ärenden,
        menyNyckel: 'arenden',
        children: [
          {
            title: 'Mina ärenden',
            path: paths.dashboard.arende.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Anmäl nytt ärende',
            path: paths.dashboard.arende.nytt,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Från boende',
            path: paths.dashboard.arende.boende,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Styrelsens ärenden',
            path: paths.dashboard.arende.styrelse,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
    ],
  },

  /**
   * Mitt boende — funktionerna en medlem faktiskt använder i vardagen.
   */
  {
    subheader: 'Mitt boende',
    items: [
      {
        // Mitt hem och Fastighet var två grenar med samma ämne — hemmet man bor i och fastigheten
        // man äger eller förvaltar. De slogs ihop hit; föreningens driftsytor ligger kvar som
        // styrelsebarn längre ned.
        //
        // ALLA på föräldern: Offerter, Mina fastigheter och Hantverkare vänder sig till den som
        // äger en fastighet, i första hand en privatperson utan roll. Föräldern måste släppa
        // igenom dem, annars stänger den hela grenen (se härledBehörigheter). Barnen bär sina
        // egna gränser — boende för hemmets ytor, styrelse för driften.
        //
        // menyNyckel 'fastighet': den gamla 'mitt-hem' migrerades in i den (Liquibase 230).
        title: 'Fastighet',
        path: paths.dashboard.fastighet.minaHem,
        icon: ICONS.fastighet,
        menyNyckel: 'fastighet',
        allowedRoles: BEHÖRIGHET.ALLA,
        children: [
          {
            title: 'Mitt hem',
            path: paths.dashboard.fastighet.minaHem,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Planlösning',
            path: paths.dashboard.fastighet.planlösning,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Planlösning V2',
            path: paths.dashboard.fastighet.planlösningV2,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Dokumentation',
            path: paths.dashboard.fastighet.dokumentation,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Bygglov',
            path: paths.dashboard.fastighet.bygglov,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Kommunikationer',
            path: paths.dashboard.fastighet.kommunikationer,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Skapa',
            path: paths.dashboard.fastighet.skapa,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Foton',
            path: paths.dashboard.fastighet.foton,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Demo React 3 fiber',
            path: paths.dashboard.fastighet.demoReactThreeFiber,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Värme',
            path: paths.dashboard.fastighet.värme,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Förbrukning (IMD)',
            path: paths.dashboard.fastighet.imd,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'El',
            path: paths.dashboard.fastighet.el,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Värmepump',
            path: paths.dashboard.fastighet.värmepump,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Våra byggnader (3D)',
            path: paths.dashboard.fastighet.byggnader.root,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Skapa 3D-modell',
            path: paths.dashboard.fastighet.byggnader.ny,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Ritningar (3D)',
            path: paths.dashboard.fastighet.ritning.root,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Ny ritning',
            path: paths.dashboard.fastighet.ritning.ny,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Offerter',
            path: paths.dashboard.fastighet.offerter.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Mina fastigheter',
            path: paths.dashboard.fastighet.minaFastigheter,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Energideklaration',
            path: paths.dashboard.fastighet.energideklaration,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Hantverkare',
            path: paths.dashboard.fastighet.hantverkare,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
        ],
      },
      {
        title: 'Utlåning',
        path: paths.dashboard.boende.utlåningspool.root,
        icon: ICONS.bokaLåna,
        menyNyckel: 'boka-lana',
        children: [
          {
            title: 'Tvättstuga',
            path: paths.dashboard.boende.bokaTvattstuga,
            menyNyckel: 'boka-lana-tvattstuga',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Mina bokningar',
            path: paths.dashboard.boende.utlåningspool.minaBokningar,
            menyNyckel: 'utlaningspool-mina',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Inköpsförslag',
            path: paths.dashboard.boende.utlåningspool.Inköpsförslag,
            menyNyckel: 'utlaningspool-inkop',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Importen når man från Utlåningspool-vyns egen Importera-knapp,
            // så den behöver ingen egen menypost.
            title: 'Admin',
            path: paths.dashboard.boende.utlåningspool.admin,
            menyNyckel: 'utlaningspool-admin',
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
      {
        // Allt som står parkerat eller rullar. Bröts ut ur "Boka & låna" — bilpool och
        // parkering hör ihop med varandra, inte med tvättstugan.
        title: 'Fordon',
        path: paths.dashboard.boende.garage,
        icon: ICONS.fordon,
        menyNyckel: 'fordon',
        children: [
          {
            title: 'Garage & parkering',
            path: paths.dashboard.boende.garage,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Bilpool',
            path: paths.dashboard.boende.bilpool,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Lådcykelpool',
            path: paths.dashboard.boende.lådcykelpool,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            title: 'Släp',
            path: paths.dashboard.boende.släp,
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
          {
            // Ingen bil som föreningen äger, men samma fråga för den boende: hur tar jag mig
            // dit. Egen menyNyckel så att en förening kan stänga av just samåkningen utan att
            // förlora bilpoolen.
            title: 'Samåkning',
            path: paths.dashboard.boende.samåkning.root,
            menyNyckel: 'samakning',
            allowedRoles: BEHÖRIGHET.BOENDE,
          },
        ],
      },
      {
        title: 'Karta',
        path: paths.dashboard.karta.root,
        icon: ICONS.karta,
        menyNyckel: 'karta',
        children: [
          {
            title: 'Utforska',
            path: paths.dashboard.karta.root,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Mina platser',
            path: paths.dashboard.karta.platser,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            title: 'Senaste',
            path: paths.dashboard.karta.senaste,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
        ],
      },
    ],
  },

  /**
   * Föreningen — det gemensamma. Öppet för medlemmar högst upp, styrelsens
   * arbetsytor under.
   */
  {
    subheader: 'Föreningen',
    items: [
      {
        title: 'Dokument',
        path: paths.dashboard.dokument.root,
        icon: ICONS.dokument,
        menyNyckel: 'dokument',
        allowedRoles: BEHÖRIGHET.ALLA,
      },
      {
        // Fakta om den förening som är vald just nu — stadgarna, grunduppgifterna,
        // vem som är vad och den senaste årsredovisningen.
        //
        // Kontots egna föreningsrelationer (vilka organisationer användaren
        // tillhör, registrera en ny, gå med i en befintlig) hör INTE hemma här:
        // sidomenyn är scopad till den valda organisationen och filtreras dessutom
        // på `menyNyckel`, så en förening som stänger av den här grenen skulle
        // annars ta bort användarens väg in i en *annan* förening. De ligger i
        // stället på kontoytorna — kontodrawern och organisationsväljaren, båda i
        // headern — och samlas på /organisation/mina.
        title: 'Om föreningen',
        path: paths.dashboard.organisation.grunddata,
        icon: ICONS.förening,
        menyNyckel: 'foreningen',
        children: [
          {
            // Alla medlemmar har rätt att läsa stadgarna — redigering är gated i
            // vyn och i backend till styrelse/admin.
            title: 'Stadgar',
            path: paths.dashboard.organisation.stadgar,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Grunduppgifterna ur årsredovisningen. Läsbara för alla medlemmar,
            // men bara styrelsen kan läsa in en ny årsredovisning — det är
            // gated i vyn och i backend.
            title: 'Grunduppgifter',
            path: paths.dashboard.organisation.grunddata,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
          {
            // Vem som är vad i föreningen är inget hemligt för dess medlemmar —
            // listan är öppen, men bara admin kan ändra i den.
            title: 'Medlemmar & kontakter',
            path: paths.dashboard.organisation.anvandare,
            allowedRoles: ['medlem', 'styrelseledamot', 'egenföretagare'],
          },
          {
            title: 'Årsredovisning',
            path: paths.dashboard.ekonomi.arsredovisningar.senaste,
            allowedRoles: BEHÖRIGHET.ALLA,
          },
        ],
      },
      {
        title: 'Styrelsen',
        path: paths.dashboard.styrelse.nastaMote,
        icon: ICONS.styrelse,
        menyNyckel: 'styrelse',
        allowedRoles: BEHÖRIGHET.STYRELSE,
        children: [
          {
            title: 'Nästa möte',
            path: paths.dashboard.styrelse.nastaMote,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Ledamoter',
            path: paths.dashboard.styrelse.ledamoter,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Arkiv',
            path: paths.dashboard.styrelse.arkiv.root,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
      {
        title: 'Medlemsregister',
        // v2 är det aktuella registret (Hushåll + Person). Den äldre vyn på
        // `medlemmar.root` ligger kvar som rutt men länkas inte längre.
        path: paths.dashboard.medlemmar.v2,
        icon: ICONS.medlemmar,
        menyNyckel: 'medlemmar',
        allowedRoles: BEHÖRIGHET.STYRELSE,
        children: [
          {
            title: 'Register',
            path: paths.dashboard.medlemmar.v2,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Importera',
            path: paths.dashboard.medlemmar.importera,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Avgifter',
            path: paths.dashboard.medlemmar.avgifter,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Månadsavier',
            path: paths.dashboard.medlemmar.månadsfakturor,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Användare & roller',
            path: paths.dashboard.organisation_users,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
        ],
      },
    ],
  },

  /**
   * Ekonomi — hela sektionen kräver behörigheten `ekonomi`, som styrelse,
   * kassör, ekonomiassistent, förvaltare och egenföretagare får via mappningen.
   */
  {
    subheader: 'Ekonomi',
    items: [
      {
        title: 'Översikt',
        path: paths.dashboard.ekonomi.root,
        icon: ICONS.ekonomi,
        menyNyckel: 'ekonomi-oversikt',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
      {
        title: 'Bank',
        path: paths.dashboard.ekonomi.bank.transaktionskonto.root,
        icon: ICONS.ekonomi,
        menyNyckel: 'bank',
        children: [
          {
            title: 'Transaktionskonto',
            path: paths.dashboard.ekonomi.bank.transaktionskonto.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Bankkonton',
            path: paths.dashboard.ekonomi.bank.konton.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Bokföring',
        path: paths.dashboard.ekonomi.bokforing.root,
        icon: ICONS.bokföring,
        menyNyckel: 'bokforing',
        children: [
          {
            title: 'Bokför',
            path: paths.dashboard.ekonomi.bokforing.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Bokförda händelser',
            path: paths.dashboard.ekonomi.bokforing.händelser.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Verifikationer',
            path: paths.dashboard.ekonomi.bokforing.verifikationer.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Huvudbok',
            path: paths.dashboard.ekonomi.bokforing.huvudbok,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Grundbok',
            path: paths.dashboard.ekonomi.bokforing.grundbok,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Importera',
            path: paths.dashboard.ekonomi.bokforing.bokslut.importera,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Exportera',
            path: paths.dashboard.ekonomi.bokforing.exportera,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Aktieutdelning',
            path: paths.dashboard.aktieutdelning.root,
            menyNyckel: 'aktieutdelning',
            allowedRoles: BEHÖRIGHET.KONSULT,
          },
        ],
      },
      {
        title: 'Rapporter & bokslut',
        path: paths.dashboard.ekonomi.bokforing.resultaträkning,
        icon: ICONS.rapporter,
        menyNyckel: 'rapporter',
        children: [
          {
            title: 'Resultaträkning',
            path: paths.dashboard.ekonomi.bokforing.resultaträkning,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Balansräkning',
            path: paths.dashboard.ekonomi.bokforing.balansräkning,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Bokslut',
            path: paths.dashboard.ekonomi.bokforing.bokslut.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Årsredovisning',
            path: paths.dashboard.ekonomi.arsredovisningar.senaste,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Arkiv',
            path: paths.dashboard.ekonomi.arsredovisningar.arkiv,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Utkast för ny',
            path: paths.dashboard.ekonomi.arsredovisningar.utkast,
            allowedRoles: BEHÖRIGHET.STYRELSE,
          },
          {
            title: 'Sök hos Bolagsverket',
            path: paths.dashboard.ekonomi.arsredovisningar.sokBolagsverket,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Fakturor',
        path: paths.dashboard.ekonomi.fakturor.kundfakturor,
        icon: ICONS.fakturor,
        menyNyckel: 'fakturor',
        children: [
          {
            title: 'Kundfakturor',
            path: paths.dashboard.ekonomi.fakturor.kundfakturor,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Leverantörsfakturor',
            path: paths.dashboard.ekonomi.bokforing.händelser.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Mottagna e-postfakturor',
            path: paths.dashboard.ekonomi.mottagnaEmail,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'E-postinställningar',
            path: paths.dashboard.ekonomi.emailInställningar,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
        ],
      },
      {
        title: 'Lön',
        path: paths.dashboard.ekonomi.loner.root,
        icon: ICONS.lön,
        menyNyckel: 'lon',
        children: [
          {
            title: 'Lönekörningar',
            path: paths.dashboard.ekonomi.loner.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Ny lön',
            path: paths.dashboard.ekonomi.loner.ny,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Kommande',
            path: paths.dashboard.ekonomi.loner.kommande,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Utbetalda',
            path: paths.dashboard.ekonomi.loner.utbetalda,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Skatt',
        path: paths.dashboard.ekonomi.skatter.root,
        icon: ICONS.skattLöner,
        menyNyckel: 'skatt',
        children: [
          {
            title: 'Skattekonto',
            path: paths.dashboard.ekonomi.skatter.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Moms',
            path: paths.dashboard.ekonomi.moms.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Ny momsdeklaration',
            path: paths.dashboard.ekonomi.moms.ny,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Momsdeklarationer',
            path: paths.dashboard.ekonomi.moms.momsdeklarationer.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        title: 'Försäkringar',
        path: paths.dashboard.ekonomi.forsakringar.root,
        icon: ICONS.försäkringar,
        menyNyckel: 'forsakringar',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
      {
        title: 'Kapitaltillskott',
        path: paths.dashboard.ekonomi.kapitaltillskott,
        icon: ICONS.fastighetslån,
        menyNyckel: 'kapitaltillskott',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
      {
        // Bara föräldern bär menyNyckel: nyckeln är den som slås av och på per förening, och
        // två poster med samma nyckel gör katalogen tvetydig.
        title: 'Fastighetslån',
        path: paths.dashboard.ekonomi.fastighetslan.root,
        icon: ICONS.fastighetslån,
        menyNyckel: 'fastighetslan',
        allowedRoles: BEHÖRIGHET.EKONOMI,
        children: [
          {
            title: 'Översikt',
            path: paths.dashboard.ekonomi.fastighetslan.root,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
          {
            title: 'Översikt V2',
            path: paths.dashboard.ekonomi.fastighetslan.v2,
            allowedRoles: BEHÖRIGHET.EKONOMI,
          },
        ],
      },
      {
        // Föräldern behöver egna allowedRoles: sidebaren filtreras både på trädet och i
        // layout.tsx, och en ogated förälder läcker att ytan finns.
        title: 'Budget & prognos',
        path: paths.dashboard.ekonomi.budget.root,
        icon: ICONS.ekonomi,
        menyNyckel: 'budget',
        allowedRoles: BEHÖRIGHET.EKONOMI,
      },
    ],
  },

  /**
   * Arborist — egen yrkesroll, inget en föreningsmedlem ska möta.
   */
  {
    subheader: 'Arborist',
    items: [
      {
        title: 'Trädvård',
        path: paths.dashboard.arborist.tradvardsplaner,
        icon: ICONS.arborist,
        menyNyckel: 'arborist',
        // Yrkesyta som bara angår enstaka föreningar — av som standard, slås på
        // i organisationens menyinställningar.
        standardDold: true,
        allowedRoles: BEHÖRIGHET.ARBORIST,
        children: [
          {
            title: 'Trädvårdsplaner',
            path: paths.dashboard.arborist.tradvardsplaner,
            allowedRoles: BEHÖRIGHET.ARBORIST,
          },
          {
            title: 'Arborist-pool',
            path: paths.dashboard.arboristPool.root,
            allowedRoles: BEHÖRIGHET.ARBORIST,
          },
          {
            title: 'Skapa offert',
            path: paths.dashboard.offert.trädgårdsoffert,
            allowedRoles: BEHÖRIGHET.ARBORIST,
          },
        ],
      },
    ],
  },

  /**
   * Administration — hårda grindar. Föräldrarna har egna `allowedRoles` så att
   * inget barn kan öppna dem av misstag.
   */
  {
    subheader: 'Administration',
    items: [
      {
        title: 'Inställningar',
        path: paths.dashboard.organisation.installningar,
        icon: ICONS.inställningar,
        menyNyckel: 'installningar',
        allowedRoles: BEHÖRIGHET.ADMIN,
        children: [
          {
            title: 'Organisationsinställningar',
            path: paths.dashboard.organisation.installningar,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
          {
            title: 'Backup',
            path: paths.dashboard.backup.root,
            allowedRoles: BEHÖRIGHET.ADMIN,
          },
        ],
      },
      {
        title: 'System',
        path: paths.dashboard.admin.roller.root,
        icon: ICONS.system,
        allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
        children: [
          {
            title: 'Roller',
            path: paths.dashboard.admin.roller.root,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            // Rolldefinitionerna ovan beskriver vilka roller som finns; den här sätter
            // dem på faktiska användare, i vilken organisation som helst.
            title: 'Användarroller',
            path: paths.dashboard.superadmin.anvandarroller,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Förhandsskapade föreningar',
            path: paths.dashboard.superadmin.organisationer,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Databas',
            path: paths.dashboard.admin.database,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Bokförings-admin',
            path: paths.dashboard.ekonomi.bokforing.admin.root,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Mock-data',
            path: paths.dashboard.superadmin.mock,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Fellogg',
            path: paths.dashboard.superadmin.fel,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
          {
            title: 'Rate limiting',
            path: paths.dashboard.superadmin.rateLimiting,
            allowedRoles: BEHÖRIGHET.SUPER_ADMIN,
          },
        ],
      },
    ],
  },
];

/**
 * Trädet med härledda föräldrabehörigheter. Det är detta som faktiskt filtreras.
 */
export const sidebarsFörBrfAppOchKonsultApp: NavSectionProps['data'] = NAVIGERING.map((grupp) => ({
  ...grupp,
  items: härledBehörigheter(grupp.items),
}));

/**
 * Katalogen över de toppnivåmenyer som kan slås av/på per organisation.
 * Härleds ur trädet ovan — sätt en `menyNyckel` på en toppnivåpost så dyker den
 * upp i organisationens inställningsvy automatiskt.
 */
export const MENY_KATALOG = byggMenyKatalog(sidebarsFörBrfAppOchKonsultApp);

export const MENY_TRÄD = byggMenyTräd(sidebarsFörBrfAppOchKonsultApp);

export function hämtaSidebarsFörAnvändare(
  anvandare: IAnvändare | undefined,
  menySynlighet?: Record<string, boolean>
): NavGroupProps[] {
  return filtreraNavigering(sidebarsFörBrfAppOchKonsultApp, anvandare, menySynlighet);
}
